import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Calendar, RefreshCcw } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';

const CancelledSubscriptionBanner = ({ className = '' }) => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]); // Recarrega quando objeto user muda (não apenas user.id)

  const fetchStatus = async () => {
    try {
      const response = await api.get('/subscription/usage-status', {validateStatus: s => s === 200 || s === 401});
      if (response.status === 401) return; // Não logado, ignorar
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleReactivate = async () => {
    try {
      setReactivating(true);
      await api.post('/subscription/reactivate');
      toast.success('Assinatura reativada com sucesso!');
      
      // Refresh user e recarregar dados
      await refreshUser();
      await fetchStatus();
    } catch (error) {
      console.error('Error reactivating:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reativar');
    } finally {
      setReactivating(false);
    }
  };

  if (loading || !subscriptionData) return null;

  const { subscription } = subscriptionData;
  
  if (subscription.status !== 'cancelled' && subscription.status !== 'cancelled_trial') {
    return null;
  }

  const premiumUntil = subscription.premium_until || subscription.subscription_ends_at;
  if (!premiumUntil) return null;

  const endDate = new Date(premiumUntil);
  const now = new Date();
  if (endDate <= now) {
    return null;
  }

  const planName = subscription.plan === 'family' ? 'Familiar' : 'Premium';
  const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

  return (
    <Card className={`p-4 border-2 border-yellow-300 bg-yellow-50 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-yellow-900 mb-1">
            ⚠️ {t('cancelled.title')}
          </h3>
          <p className="text-sm text-yellow-800 mb-2">
            {t('cancelled.stillHaveAccess')} <strong>{planName} {t('cancelled.fullAccess')}</strong> {t('cancelled.until')}:
          </p>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Calendar className="w-4 h-4 text-yellow-700" />
            <span className="font-bold text-yellow-900">
              {endDate.toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <span className="text-sm text-yellow-700">
              ({daysRemaining} {daysRemaining === 1 ? t('cancelled.dayRemaining') : t('cancelled.daysRemaining')})
            </span>
          </div>
          <p className="text-xs text-yellow-700">
            {t('cancelled.afterDate')} <strong>{t('cancelled.freePlan')}</strong> {t('cancelled.withLimits')}.
          </p>
        </div>
        <Button
          onClick={handleReactivate}
          disabled={reactivating}
          size="sm"
          variant="outline"
          className="border-yellow-600 text-yellow-700 hover:bg-yellow-100 whitespace-nowrap"
        >
          {reactivating ? (
            <>
              <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin mr-2" />
              Reativando...
            </>
          ) : (
            <>
              <RefreshCcw className="w-4 h-4 mr-2" />
              {t('cancelled.reactivate')}
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default CancelledSubscriptionBanner;
